import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetAllLocationsQuery, useUpdateLocationBoundsMutation, adminApi } from '../redux/adminApiSlice';
import { useDispatch } from 'react-redux';

interface CalibrationPoint {
  id: string;
  name: string;
  imageX: number;
  imageY: number;
  realLat: number;
  realLng: number;
  corner: string; // 'NW', 'NE', 'SW', 'SE'
}

interface BoundaryPoint {
  x: number;
  y: number;
}

interface NeighborhoodBoundary {
  name: string;
  points: BoundaryPoint[];
  gpsPolygon?: { lat: number; lng: number }[];
}

type Mode = 'calibration' | 'tracing' | 'review';

const REFERENCE_NEIGHBORHOODS = ['Central Park', 'Hudson Yards', "Hell's Kitchen"];

const ImageCalibrator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [mode, setMode] = useState<Mode>('calibration');
  const [calibrationPoints, setCalibrationPoints] = useState<CalibrationPoint[]>([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>('');
  const [selectedCorner, setSelectedCorner] = useState<string>('NW');
  const [boundaries, setBoundaries] = useState<NeighborhoodBoundary[]>([]);
  const [currentBoundary, setCurrentBoundary] = useState<BoundaryPoint[]>([]);
  const [tracingNeighborhood, setTracingNeighborhood] = useState<string>('');
  const [isCalibrated, setIsCalibrated] = useState(false);
  const [transformMatrix, setTransformMatrix] = useState<any>(null);
  
  const { data: allLocations, refetch: refetchLocations } = useGetAllLocationsQuery();
  const [updateLocationBounds] = useUpdateLocationBoundsMutation();
  const dispatch = useDispatch();

  // Find NYC and reference neighborhoods
  const nycLocation = allLocations?.find(loc => 
    (loc.name.toLowerCase().includes('new york city') || loc.name.toLowerCase() === 'new york city') && 
    (loc.level === 'CITY' || loc.level === 'City')
  );

  const referenceNeighborhoods = allLocations?.filter(loc => 
    (loc.level === 'NEIGHBORHOOD' || loc.level === 'Neighborhood') &&
    loc.parent === nycLocation?._id &&
    REFERENCE_NEIGHBORHOODS.some(ref => loc.name.toLowerCase().includes(ref.toLowerCase()))
  ) || [];

  const allManhattanNeighborhoods = allLocations?.filter(loc => 
    (loc.level === 'NEIGHBORHOOD' || loc.level === 'Neighborhood') &&
    loc.parent === nycLocation?._id
  ) || [];

  // Get sorted list for display
  const sortedManhattanNeighborhoods = allManhattanNeighborhoods
    .sort((a, b) => a.name.localeCompare(b.name));

  // Load calibration from localStorage on mount
  useEffect(() => {
    const savedCalibration = localStorage.getItem('imageCalibrator_calibration');
    if (savedCalibration) {
      try {
        const parsed = JSON.parse(savedCalibration);
        setCalibrationPoints(parsed.calibrationPoints || []);
        setTransformMatrix(parsed.transformMatrix || null);
        setIsCalibrated(parsed.isCalibrated || false);
        console.log('[ImageCalibrator] Loaded saved calibration:', parsed);
      } catch (error) {
        console.error('[ImageCalibrator] Error loading saved calibration:', error);
      }
    }
  }, []);

  // Save calibration to localStorage whenever it changes
  useEffect(() => {
    if (calibrationPoints.length > 0 || transformMatrix) {
      const calibrationData = {
        calibrationPoints,
        transformMatrix,
        isCalibrated,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('imageCalibrator_calibration', JSON.stringify(calibrationData));
      console.log('[ImageCalibrator] Saved calibration to localStorage');
    }
  }, [calibrationPoints, transformMatrix, isCalibrated]);

  // Load and setup image
  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the image
      ctx.drawImage(img, 0, 0);
      
      // Draw existing calibration points
      drawCalibrationPoints(ctx);
      
      // Draw current boundary being traced
      if (currentBoundary.length > 0) {
        drawBoundary(ctx, currentBoundary, '#ff0000', false);
      }
      
      // Draw completed boundaries
      boundaries.forEach((boundary, index) => {
        drawBoundary(ctx, boundary.points, `hsl(${index * 60}, 70%, 50%)`, true);
      });
    };
    
    img.src = '/Manhattan-Neighborhoods.webp';
  }, [calibrationPoints, currentBoundary, boundaries]); // eslint-disable-line react-hooks/exhaustive-deps

  const drawCalibrationPoints = (ctx: CanvasRenderingContext2D) => {
    calibrationPoints.forEach(point => {
      ctx.fillStyle = '#00ff00';
      ctx.beginPath();
      ctx.arc(point.imageX, point.imageY, 8, 0, 2 * Math.PI);
      ctx.fill();
      
      // Label
      ctx.fillStyle = '#000000';
      ctx.font = '12px Arial';
      ctx.fillText(`${point.name} ${point.corner}`, point.imageX + 10, point.imageY - 10);
    });
  };

  const drawBoundary = (ctx: CanvasRenderingContext2D, points: BoundaryPoint[], color: string, closed: boolean) => {
    if (points.length < 2) return;
    
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    
    if (closed && points.length > 2) {
      ctx.lineTo(points[0].x, points[0].y);
    }
    
    ctx.stroke();
    
    // Draw points
    points.forEach((point, index) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      if (index === 0) {
        // Draw larger circle for start point
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
        ctx.stroke();
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '10px Arial';
        ctx.fillText('START', point.x + 10, point.y - 10);
      }
    });
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (mode === 'calibration') {
      handleCalibrationClick(x, y);
    } else if (mode === 'tracing') {
      handleTracingClick(x, y);
    }
  };

  const handleCalibrationClick = (x: number, y: number) => {
    if (!selectedNeighborhood) {
      alert('Please select a reference neighborhood first');
      return;
    }

    const neighborhood = referenceNeighborhoods.find(n => n._id === selectedNeighborhood);
    if (!neighborhood || !neighborhood.boundingBox) {
      alert('Selected neighborhood does not have bounds in database');
      return;
    }

    // Extract real coordinates from the neighborhood's bounding box
    const coords = neighborhood.boundingBox.coordinates[0];
    const lats = coords.map(c => c[1]);
    const lngs = coords.map(c => c[0]);
    
    let realLat, realLng;
    
    switch (selectedCorner) {
      case 'NW':
        realLat = Math.max(...lats);
        realLng = Math.min(...lngs);
        break;
      case 'NE':
        realLat = Math.max(...lats);
        realLng = Math.max(...lngs);
        break;
      case 'SW':
        realLat = Math.min(...lats);
        realLng = Math.min(...lngs);
        break;
      case 'SE':
        realLat = Math.min(...lats);
        realLng = Math.max(...lngs);
        break;
      default:
        return;
    }

    const newPoint: CalibrationPoint = {
      id: `${neighborhood.name}-${selectedCorner}`,
      name: neighborhood.name,
      imageX: x,
      imageY: y,
      realLat,
      realLng,
      corner: selectedCorner
    };

    setCalibrationPoints(prev => {
      const filtered = prev.filter(p => p.id !== newPoint.id);
      return [...filtered, newPoint];
    });

    console.log(`[ImageCalibrator] Added calibration point:`, newPoint);
  };

  const handleTracingClick = (x: number, y: number) => {
    if (!tracingNeighborhood) {
      alert('Please enter a neighborhood name first');
      return;
    }

    // Check if clicking near the first point to close the polygon
    if (currentBoundary.length >= 3) {
      const firstPoint = currentBoundary[0];
      const distance = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));
      
      // If clicking within 10 pixels of the first point, close the polygon
      if (distance <= 10) {
        console.log('[ImageCalibrator] Closing polygon by clicking near start point');
        finishBoundary();
        return;
      }
    }

    setCurrentBoundary(prev => [...prev, { x, y }]);
  };

  const calculateTransformation = () => {
    if (calibrationPoints.length < 3) {
      alert('Need at least 3 calibration points to calculate transformation');
      return;
    }

    console.log('[ImageCalibrator] Calculating transformation with points:', calibrationPoints);
    
    // Calculate bounding box of calibration points in both coordinate systems
    const imagePoints = calibrationPoints.map(p => ({ x: p.imageX, y: p.imageY }));
    const gpsPoints = calibrationPoints.map(p => ({ lat: p.realLat, lng: p.realLng }));
    
    // Find bounds in image coordinates
    const imageMinX = Math.min(...imagePoints.map(p => p.x));
    const imageMaxX = Math.max(...imagePoints.map(p => p.x));
    const imageMinY = Math.min(...imagePoints.map(p => p.y));
    const imageMaxY = Math.max(...imagePoints.map(p => p.y));
    
    // Find bounds in GPS coordinates
    const gpsMinLat = Math.min(...gpsPoints.map(p => p.lat));
    const gpsMaxLat = Math.max(...gpsPoints.map(p => p.lat));
    const gpsMinLng = Math.min(...gpsPoints.map(p => p.lng));
    const gpsMaxLng = Math.max(...gpsPoints.map(p => p.lng));
    
    // Calculate scale factors
    const scaleX = (gpsMaxLng - gpsMinLng) / (imageMaxX - imageMinX);
    const scaleY = (gpsMaxLat - gpsMinLat) / (imageMaxY - imageMinY);
    
    const transformMatrix = {
      imageMinX,
      imageMinY,
      gpsMinLng,
      gpsMinLat,
      scaleX,
      scaleY,
      calibrationPoints: [...calibrationPoints],
      calculated: true
    };
    
    console.log('[ImageCalibrator] Transformation matrix:', transformMatrix);
    
    // Test the calibration accuracy
    console.log('[ImageCalibrator] Testing calibration accuracy:');
    calibrationPoints.forEach(point => {
      const converted = {
        lng: gpsMinLng + ((point.imageX - imageMinX) * scaleX),
        lat: gpsMinLat + ((point.imageY - imageMinY) * scaleY)
      };
      const error = {
        lat: Math.abs(converted.lat - point.realLat),
        lng: Math.abs(converted.lng - point.realLng)
      };
      console.log(`  ${point.name} ${point.corner}: Expected (${point.realLat}, ${point.realLng}) → Got (${converted.lat}, ${converted.lng}) → Error: (${error.lat}, ${error.lng})`);
    });
    
    setTransformMatrix(transformMatrix);
    setIsCalibrated(true);
  };

  const convertPixelToGPS = (x: number, y: number): { lat: number; lng: number } => {
    if (!transformMatrix || !transformMatrix.calculated) {
      throw new Error('Transformation matrix not calculated');
    }

    // Apply linear transformation using the calculated matrix
    const normalizedX = (x - transformMatrix.imageMinX);
    const normalizedY = (y - transformMatrix.imageMinY);
    
    const lng = transformMatrix.gpsMinLng + (normalizedX * transformMatrix.scaleX);
    const lat = transformMatrix.gpsMinLat + (normalizedY * transformMatrix.scaleY);
    
    console.log(`[ImageCalibrator] Converting pixel (${x}, ${y}) to GPS (${lat}, ${lng})`);
    
    return { lat, lng };
  };

  const finishBoundary = () => {
    if (currentBoundary.length < 3) {
      alert('Need at least 3 points to create a boundary');
      return;
    }

    if (!isCalibrated) {
      alert('Please calibrate the image first');
      return;
    }

    try {
      // Convert pixel coordinates to GPS coordinates
      const gpsPolygon = currentBoundary.map(point => convertPixelToGPS(point.x, point.y));
      
      const newBoundary: NeighborhoodBoundary = {
        name: tracingNeighborhood,
        points: [...currentBoundary],
        gpsPolygon
      };

      setBoundaries(prev => [...prev, newBoundary]);
      setCurrentBoundary([]);
      setTracingNeighborhood('');
      
      console.log('[ImageCalibrator] Created boundary for:', newBoundary.name, gpsPolygon);
    } catch (error) {
      console.error('[ImageCalibrator] Error converting boundary:', error);
      alert('Error converting boundary to GPS coordinates');
    }
  };

  const manuallyUpdateInwood = async () => {
    // Simple rectangular bounding box for Inwood (guaranteed non-intersecting)
    // Based on the traced coordinates but simplified to avoid self-intersection
    const minLng = -73.98182834713387; // Easternmost point
    const maxLng = -73.98086525348832; // Westernmost point  
    const minLat = 40.78065704883917;  // Southernmost point
    const maxLat = 40.78181461967209;  // Northernmost point
    
    const inwoodCoordinates = [
      [minLng, minLat], // Southeast corner
      [maxLng, minLat], // Southwest corner
      [maxLng, maxLat], // Northwest corner
      [minLng, maxLat], // Northeast corner
      [minLng, minLat]  // Close polygon
    ];

    const inwoodLocation = allManhattanNeighborhoods.find(n => 
      n.name.toLowerCase().includes('inwood')
    );

    if (!inwoodLocation) {
      alert('Inwood location not found in database');
      return;
    }

    console.log('[Manual Update] Updating Inwood with ID:', inwoodLocation._id);
    console.log('[Manual Update] Coordinates:', inwoodCoordinates);

    try {
      const result = await updateLocationBounds({
        id: inwoodLocation._id,
        boundingBox: {
          type: 'Polygon',
          coordinates: [inwoodCoordinates]
        }
      }).unwrap();

      console.log('[Manual Update] Success:', result);
      alert('Inwood bounding box updated successfully!');
      
      // Force cache refresh
      dispatch(adminApi.util.invalidateTags(['Locations']));
      await refetchLocations();
    } catch (error) {
      console.error('[Manual Update] Error:', error);
      alert('Failed to update Inwood: ' + JSON.stringify(error));
    }
  };

  const saveBoundariesToDatabase = async () => {
    if (boundaries.length === 0) {
      alert('No boundaries to save');
      return;
    }

    console.log('[ImageCalibrator] Starting to save boundaries:', boundaries.map(b => b.name));
    console.log(`[ImageCalibrator] Found ${allManhattanNeighborhoods.length} neighborhoods in NYC:`, allManhattanNeighborhoods.map(n => n.name).sort());

    for (const boundary of boundaries) {
      if (!boundary.gpsPolygon) continue;
      
      try {
        console.log(`[ImageCalibrator] Looking for neighborhood: "${boundary.name}"`);
        
        // Find the neighborhood in the database with flexible matching
        let neighborhood = allManhattanNeighborhoods.find(n => 
          n.name.toLowerCase() === boundary.name.toLowerCase()
        );
        
        // If exact match fails, try partial matching
        if (!neighborhood) {
          neighborhood = allManhattanNeighborhoods.find(n => 
            n.name.toLowerCase().includes(boundary.name.toLowerCase()) ||
            boundary.name.toLowerCase().includes(n.name.toLowerCase())
          );
        }
        
        if (!neighborhood) {
          console.error(`[ImageCalibrator] Neighborhood not found in database: "${boundary.name}"`);
          console.log('[ImageCalibrator] Available options:', allManhattanNeighborhoods.map(n => n.name));
          continue;
        }

        console.log(`[ImageCalibrator] Found matching neighborhood: "${neighborhood.name}" (ID: ${neighborhood._id})`);

        // Convert GPS polygon to database format
        const coordinates = boundary.gpsPolygon.map(point => [point.lng, point.lat]);
        coordinates.push(coordinates[0]); // Close the polygon
        
        const boundingBox = {
          type: 'Polygon',
          coordinates: [coordinates]
        };

        console.log(`[ImageCalibrator] Updating neighborhood ${neighborhood._id} with boundingBox:`, boundingBox);
        await updateLocationBounds({ id: neighborhood._id, boundingBox }).unwrap();
        console.log(`[ImageCalibrator] Successfully updated bounds for: ${boundary.name} -> ${neighborhood.name}`);
        
      } catch (error) {
        console.error(`[ImageCalibrator] Failed to update ${boundary.name}:`, error);
      }
    }
    
    // Force invalidate ALL location-related cache entries
    dispatch(adminApi.util.invalidateTags(['Locations']));
    console.log('[ImageCalibrator] Invalidated all Locations cache tags');
    
    // Also refetch this specific query
    await refetchLocations();
    console.log('[ImageCalibrator] Refreshed locations data after updates');
    
    alert(`Successfully updated ${boundaries.length} neighborhood boundaries! The changes should now be visible in the admin console.`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Interactive Image Calibrator
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Use your authoritative Manhattan map to create precise neighborhood boundaries.
      </Typography>

      {/* Mode Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Mode: {mode.charAt(0).toUpperCase() + mode.slice(1)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant={mode === 'calibration' ? 'contained' : 'outlined'}
            onClick={() => setMode('calibration')}
          >
            1. Calibration
          </Button>
          <Button 
            variant={mode === 'tracing' ? 'contained' : 'outlined'}
            onClick={() => setMode('tracing')}
            disabled={!isCalibrated}
          >
            2. Boundary Tracing
          </Button>
          <Button 
            variant={mode === 'review' ? 'contained' : 'outlined'}
            onClick={() => setMode('review')}
          >
            3. Review & Save
          </Button>
        </Box>
      </Paper>

      {/* Calibration Mode */}
      {mode === 'calibration' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 1: Calibrate Image Coordinates
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Click on the corners of reference neighborhoods to calibrate the coordinate system.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Reference Neighborhood</InputLabel>
              <Select
                value={selectedNeighborhood}
                onChange={(e) => setSelectedNeighborhood(e.target.value)}
              >
                {referenceNeighborhoods.map(neighborhood => (
                  <MenuItem key={neighborhood._id} value={neighborhood._id}>
                    {neighborhood.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Corner</InputLabel>
              <Select
                value={selectedCorner}
                onChange={(e) => setSelectedCorner(e.target.value)}
              >
                <MenuItem value="NW">Northwest</MenuItem>
                <MenuItem value="NE">Northeast</MenuItem>
                <MenuItem value="SW">Southwest</MenuItem>
                <MenuItem value="SE">Southeast</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button 
              variant="contained" 
              onClick={calculateTransformation}
              disabled={calibrationPoints.length < 3}
            >
              Calculate Transformation ({calibrationPoints.length}/3+ points)
            </Button>
            
            <Button 
              variant="outlined" 
              color="warning"
              onClick={() => {
                setCalibrationPoints([]);
                setTransformMatrix(null);
                setIsCalibrated(false);
                localStorage.removeItem('imageCalibrator_calibration');
                console.log('[ImageCalibrator] Cleared calibration');
              }}
              disabled={calibrationPoints.length === 0}
            >
              Clear Calibration
            </Button>
          </Box>

          {isCalibrated && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ✅ Image calibrated! You can now trace neighborhood boundaries.
              {transformMatrix?.savedAt && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Calibration saved: {new Date(transformMatrix.savedAt).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}

          {/* Calibration Points List */}
          {calibrationPoints.length > 0 && (
            <List dense>
              {calibrationPoints.map(point => (
                <ListItem key={point.id}>
                  <ListItemText 
                    primary={`${point.name} ${point.corner}`}
                    secondary={`Image: (${point.imageX.toFixed(0)}, ${point.imageY.toFixed(0)}) → GPS: (${point.realLat.toFixed(6)}, ${point.realLng.toFixed(6)})`}
                  />
                  <IconButton 
                    onClick={() => setCalibrationPoints(prev => prev.filter(p => p.id !== point.id))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Tracing Mode */}
      {mode === 'tracing' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 2: Trace Neighborhood Boundaries
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a neighborhood and click around its boundary. Click near the START point to close the polygon automatically.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Neighborhood</InputLabel>
              <Select
                value={tracingNeighborhood}
                onChange={(e) => setTracingNeighborhood(e.target.value)}
                label="Select Neighborhood"
              >
                {sortedManhattanNeighborhoods.map(neighborhood => (
                  <MenuItem key={neighborhood._id} value={neighborhood.name}>
                    {neighborhood.name} {neighborhood.boundingBox ? '✓' : '⚠️'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              onClick={finishBoundary}
              disabled={currentBoundary.length < 3}
            >
              Finish Boundary ({currentBoundary.length} points)
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setCurrentBoundary([])}
              disabled={currentBoundary.length === 0}
            >
              Clear Current
            </Button>
          </Box>
        </Paper>
      )}

      {/* Review Mode */}
      {mode === 'review' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 3: Review & Save Boundaries
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            {boundaries.length} boundaries ready to save to database.
          </Typography>
          
          <Button 
            variant="contained" 
            onClick={saveBoundariesToDatabase}
            disabled={boundaries.length === 0}
            sx={{ mb: 2, mr: 2 }}
          >
            Save All Boundaries to Database
          </Button>
          
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={manuallyUpdateInwood}
            sx={{ mb: 2 }}
          >
            Manual Update Inwood
          </Button>

          {boundaries.length > 0 && (
            <List>
              {boundaries.map((boundary, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={boundary.name}
                    secondary={`${boundary.points.length} points → ${boundary.gpsPolygon?.length || 0} GPS coordinates`}
                  />
                  <IconButton 
                    onClick={() => setBoundaries(prev => prev.filter((_, i) => i !== index))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Interactive Canvas */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manhattan Neighborhoods Map
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {mode === 'calibration' && 'Click on neighborhood corners to calibrate'}
          {mode === 'tracing' && 'Click around neighborhood boundaries. Click near the START point to close the polygon.'}
          {mode === 'review' && 'Review your traced boundaries'}
        </Typography>
        
        <Box sx={{ border: '1px solid #ccc', display: 'inline-block' }}>
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            style={{ 
              cursor: mode === 'review' ? 'default' : 'crosshair',
              maxWidth: '100%',
              height: 'auto'
            }}
          />
        </Box>
        
        <img 
          ref={imageRef}
          src="/Manhattan-Neighborhoods.webp"
          alt="Manhattan Neighborhoods"
          style={{ display: 'none' }}
        />
      </Paper>
    </Box>
  );
};

export default ImageCalibrator;
